import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { readServerFailure, useActiveOrg, userDisplayName } from "common-fe";
import { useScoreApi } from "../api/scoreApi";
import useOrgMembersById from "../hooks/useOrgMembersById";
import useVenueDateTime from "../hooks/useVenueDateTime";

// #3690 -- "created by mistake". A sheet (or a manual ride) that should never have existed: the
// rider rode a different test than the one the sheet was opened on, and the scribe then added a
// manual ride on yet another test. NOT a withdrawal (that is Set Outcome). The server keeps the
// sheet and its marks for the audit trail but detaches it, so it stops counting everywhere and the
// next scribe on the ride gets a fresh blank sheet. Staff errors follow ADR 0004: the server's own
// reason is shown as written, with a button to the exact thing in the way when it names one.

// `subject` is { kind: "sheet", id } or { kind: "ride", id, sheetCount }.
export function MarkCreatedByMistakeDialog({ open, subject, onClose, onChanged }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const isRide = subject?.kind === "ride";

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isRide) {
        // The dialog lists the sheets that go with the ride, so confirming it is the explicit yes.
        await scoreApi.rides.markRideCreatedByMistake(subject.id, { reason: reason.trim(), includeSheets: true });
      } else {
        await scoreApi.scoreSheets.markScoreSheetCreatedByMistake(subject.id, { reason: reason.trim() });
      }
      await onChanged();
      onClose();
    } catch (err) {
      setError(readServerFailure(err).message || t("rideEditor.createdByMistake.markError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} data-testid="created-by-mistake-dialog" fullWidth maxWidth="sm">
      <DialogTitle>{isRide ? t("rideEditor.createdByMistake.rideTitle") : t("rideEditor.createdByMistake.sheetTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isRide
            ? t("rideEditor.createdByMistake.rideBody", { count: subject?.sheetCount ?? 0 })
            : t("rideEditor.createdByMistake.sheetBody")}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={t("rideEditor.createdByMistake.reasonLabel")}
          helperText={t("rideEditor.createdByMistake.reasonHelp")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ "data-testid": "created-by-mistake-reason", maxLength: 500 }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="created-by-mistake-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy} data-testid="created-by-mistake-cancel">
          {t("rideEditor.createdByMistake.cancel")}
        </Button>
        <Button
          variant="contained"
          color="warning"
          loading={busy}
          disabled={reason.trim() === ""}
          onClick={apply}
          data-testid="created-by-mistake-apply"
        >
          {t("rideEditor.createdByMistake.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Everything already marked on this ride, with the Undo ("Put back"). `ride` is the ride row,
// `sheets` every sheet on it (contenders and mistaken ones included). Renders nothing when there is
// nothing to show. A put-back refused because a newer sheet holds the seat names that sheet and
// offers a button that opens it (`onOpenSheet`).
export default function CreatedByMistakePanel({ ride, sheets, testLabel, onChanged, onOpenSheet }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const formatWhen = useVenueDateTime();
  const { activeOrgId } = useActiveOrg();
  const membersById = useOrgMembersById(activeOrgId);
  const [busyId, setBusyId] = useState(null);
  const [failure, setFailure] = useState(null); // { message, currentSheetId }

  const mistaken = (sheets ?? []).filter((s) => s.createdByMistake);
  const retired = ride?.createdByMistake && ride?.resolvedIntoRideId ? ride.createdByMistake : null;
  if (!retired && mistaken.length === 0) return null;

  const who = (sub) => (sub ? userDisplayName(membersById[sub]) || t("rideEditor.createdByMistake.unknownPerson") : t("rideEditor.createdByMistake.unknownPerson"));

  const putBack = async (id, kind) => {
    setBusyId(id);
    setFailure(null);
    try {
      if (kind === "ride") await scoreApi.rides.restoreRideFromMistake(id);
      else await scoreApi.scoreSheets.restoreScoreSheetFromMistake(id);
      await onChanged();
    } catch (err) {
      const { message, data } = readServerFailure(err);
      setFailure({ message: message || t("rideEditor.createdByMistake.restoreError"), currentSheetId: data?.currentSheetId ?? null });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ mb: 2 }} data-testid="created-by-mistake-panel">
      {retired && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          data-testid="created-by-mistake-ride-banner"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={busyId === ride.id}
              onClick={() => putBack(ride.id, "ride")}
              data-testid="created-by-mistake-restore-ride"
            >
              {t("rideEditor.createdByMistake.putRideBack")}
            </Button>
          }
        >
          {t("rideEditor.createdByMistake.rideBanner", { who: who(retired.by), when: formatWhen(retired.at), reason: retired.reason })}
        </Alert>
      )}
      {mistaken.length > 0 && (
        <>
          <Typography variant="subtitle2" component="h3">
            {t("rideEditor.createdByMistake.panelTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("rideEditor.createdByMistake.panelBody")}
          </Typography>
          <Table size="small" data-testid="created-by-mistake-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("rideEditor.createdByMistake.colJudge")}</TableCell>
                <TableCell>{t("rideEditor.createdByMistake.colTest")}</TableCell>
                <TableCell>{t("rideEditor.createdByMistake.colBy")}</TableCell>
                <TableCell>{t("rideEditor.createdByMistake.colWhen")}</TableCell>
                <TableCell>{t("rideEditor.createdByMistake.colReason")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {mistaken.map((s) => (
                <TableRow key={s.id} data-testid="created-by-mistake-row">
                  <TableCell>{s.judgeId}</TableCell>
                  <TableCell>{testLabel(s)}</TableCell>
                  <TableCell data-testid="created-by-mistake-by">{who(s.createdByMistake.by)}</TableCell>
                  <TableCell>{formatWhen(s.createdByMistake.at)}</TableCell>
                  <TableCell>{s.createdByMistake.reason}</TableCell>
                  <TableCell align="right">
                    {/* A sheet retired along with its ride comes back with the ride. */}
                    {!(retired && s.createdByMistake.viaRideId === ride.id) && (
                      <Button
                        size="small"
                        disabled={busyId === s.id}
                        onClick={() => putBack(s.id, "sheet")}
                        data-testid="created-by-mistake-restore-sheet"
                      >
                        {t("rideEditor.createdByMistake.putBack")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
      {failure && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          data-testid="created-by-mistake-restore-error"
          action={
            failure.currentSheetId && onOpenSheet ? (
              <Button color="inherit" size="small" onClick={() => onOpenSheet(failure.currentSheetId)} data-testid="created-by-mistake-open-newer">
                {t("rideEditor.createdByMistake.openNewer")}
              </Button>
            ) : undefined
          }
        >
          {failure.message}
        </Alert>
      )}
    </Box>
  );
}
